import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, SearchField } from "@legends/ui";
import type { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import DataGrid from "./DataGrid";
import {
  listCaveEntitlements,
  revokeEntitlement,
  fetchCaveTicketTypes,
  grantCompTicket,
  type Entitlement,
  type TicketType,
} from "./api";

// Super-admin/LSC "Entitlements" support surface (ADR 0047), on the Cave-themed AG Grid
// (ADR 0156): a read-mostly view of a cave's watch entitlements (fan, ticket, status,
// purchased) with a Revoke action. Revoke needs a REASON, so it is a bespoke action
// column (a per-row button that opens the reason prompt then calls the API) rather than
// the wrapper's generic ✕ inline-delete. Revoking records the reason only — automated
// Stripe refunds are deferred (handled by hand today). Filtering + sorting come from the
// wrapper defaults (floating filters) plus a quick-filter box.

export function AdminCaveEntitlementsTab({ caveId, rootAdmin = false }: { caveId: string; rootAdmin?: boolean }) {
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const reload = useCallback(() => {
    listCaveEntitlements(caveId)
      .then(setEntitlements)
      .catch(() => setEntitlements([]));
  }, [caveId]);

  useEffect(() => reload(), [reload]);

  // Root-admin comp grant (ADR 0223): pick one of this cave's ticket types + a
  // username, grant it bypassing payment. Shown ONLY to the root account; the API
  // enforces the same gate regardless.
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [grantTT, setGrantTT] = useState("");
  const [grantUser, setGrantUser] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMsg, setGrantMsg] = useState("");

  useEffect(() => {
    if (!rootAdmin) return;
    fetchCaveTicketTypes(caveId)
      .then(setTicketTypes)
      .catch(() => setTicketTypes([]));
  }, [caveId, rootAdmin]);

  const grant = useCallback(() => {
    const tt = grantTT || ticketTypes[0]?.id;
    const user = grantUser.trim();
    if (!tt || !user) {
      setGrantMsg("Pick a ticket and enter a username.");
      return;
    }
    setGrantBusy(true);
    setGrantMsg("");
    grantCompTicket(tt, user)
      .then((r) => {
        setGrantMsg(r.mode === "owned" ? `${r.username} already holds that ticket.` : `Granted to ${r.username}.`);
        setGrantUser("");
        reload();
      })
      .catch((e) => setGrantMsg(e?.message ?? "could not grant ticket"))
      .finally(() => setGrantBusy(false));
  }, [grantTT, grantUser, ticketTypes, reload]);

  const revoke = useCallback(
    (ent: Entitlement) => {
      const reason = window.prompt(
        `Revoke ${ent.username}'s "${ent.ticketName}" entitlement?\n\nEnter a reason (required):`,
      );
      if (reason === null) return; // cancelled
      if (reason.trim() === "") {
        setError("A revocation reason is required.");
        return;
      }
      setBusy(ent.holdingId);
      setError("");
      revokeEntitlement(ent.holdingId, reason.trim())
        .then(() => reload())
        .catch((e) => setError(e?.message ?? "could not revoke entitlement"))
        .finally(() => setBusy(null));
    },
    [reload],
  );

  const columns = useMemo<ColDef<Entitlement>[]>(
    () => [
      {
        colId: "fan",
        headerName: "Fan",
        minWidth: 160,
        // Prefer a human-readable identity (name → email); the opaque SSO subject
        // (e.g. apple:…) is the last-resort fallback and is kept on hover for support
        // reference (LSC-243).
        valueGetter: (p: ValueGetterParams<Entitlement>) =>
          p.data ? p.data.displayName || p.data.email || p.data.username : "",
        cellRenderer: (p: ICellRendererParams<Entitlement>) => (
          <span title={p.data?.username}>{p.value}</span>
        ),
      },
      { field: "ticketName", headerName: "Ticket", minWidth: 150 },
      {
        colId: "status",
        headerName: "Status",
        minWidth: 170,
        valueGetter: (p: ValueGetterParams<Entitlement>) =>
          p.data?.status === "revoked" ? "Revoked" : "Active",
        cellRenderer: (p: ICellRendererParams<Entitlement>) => {
          const ent = p.data;
          if (!ent) return null;
          if (ent.status === "revoked") {
            return (
              <span style={{ color: "var(--ember)" }}>
                Revoked
                {ent.revokeReason ? (
                  <span style={{ color: "var(--text-dim)" }}> — {ent.revokeReason}</span>
                ) : null}
              </span>
            );
          }
          return <span style={{ color: "var(--gold)" }}>Active</span>;
        },
      },
      {
        field: "purchasedAt",
        headerName: "Purchased",
        minWidth: 130,
        valueFormatter: (p) => (p.value ? new Date(String(p.value)).toLocaleDateString() : "—"),
      },
      {
        colId: "action",
        headerName: "Action",
        width: 140,
        maxWidth: 160,
        sortable: false,
        filter: false,
        editable: false,
        cellRenderer: (p: ICellRendererParams<Entitlement>) => {
          const ent = p.data;
          if (!ent) return null;
          if (ent.status !== "active") {
            return <span style={{ color: "var(--text-dim)", fontSize: "var(--fs-2xs)" }}>—</span>;
          }
          return (
            <Button variant="muted" onClick={() => revoke(ent)} disabled={busy === ent.holdingId}>
              {busy === ent.holdingId ? "Revoking…" : "Revoke"}
            </Button>
          );
        },
      },
    ],
    [revoke, busy],
  );

  return (
    <div>
      <h2 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: "0 0 4px" }}>Entitlements</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-2)", margin: "0 0 16px" }}>
        Watch entitlements for this cave. <strong>Revoke</strong> removes a Fan&rsquo;s access with a recorded reason —
        the row is kept for history. Revoking does <strong>not</strong> issue a Stripe refund; handle any refund
        manually in Stripe.
      </p>

      {error && (
        <p role="alert" style={{ color: "var(--ember)", fontSize: "var(--fs-body-2)" }}>
          {error}
        </p>
      )}

      {rootAdmin && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            margin: "0 0 14px",
            padding: "12px",
            border: "1px solid var(--hairline)",
            borderRadius: "var(--radius)",
          }}
        >
          <strong style={{ color: "var(--text)", fontSize: "var(--fs-body-2)" }}>Grant a ticket</strong>
          <select
            aria-label="Ticket type to grant"
            value={grantTT || ticketTypes[0]?.id || ""}
            onChange={(e) => setGrantTT(e.target.value)}
            disabled={ticketTypes.length === 0}
          >
            {ticketTypes.length === 0 ? (
              <option value="">No ticket types in this cave</option>
            ) : (
              ticketTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))
            )}
          </select>
          <input
            aria-label="Username to grant to"
            placeholder="username"
            value={grantUser}
            onChange={(e) => setGrantUser(e.target.value)}
          />
          <Button onClick={grant} disabled={grantBusy || ticketTypes.length === 0}>
            {grantBusy ? "Granting…" : "Grant (no payment)"}
          </Button>
          {grantMsg && (
            <span role="status" style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-2)" }}>
              {grantMsg}
            </span>
          )}
        </div>
      )}

      <div style={{ margin: "0 0 10px" }}>
        <SearchField value={q} onChange={setQ} placeholder="Filter entitlements…" width={320} />
      </div>

      <DataGrid<Entitlement>
        rows={entitlements}
        columns={columns}
        getRowId={(e) => e.holdingId}
        quickFilterText={q}
        label="Entitlements"
        emptyText="No entitlements for this cave."
      />
    </div>
  );
}
